import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, Terminal, ShoppingBag, Layers, Server, Cloud, Compass,
  Zap, Search, TrendingUp, RefreshCw, PlugZap,
  MessageSquare, PackageSearch, Sparkles, BarChart3,
} from "lucide-react";
import { JHALA_GLOBAL_CSS, TopStrip, Header, Footer, Reveal, TagChip } from "components/jhala/Layout.js";

const ECOM_SERVICES = [
  { icon: ShoppingBag, tag: "#STORE-SETUP", title: "Shopify Store Setup", desc: "Theme selection, product setup, payments, and shipping — launch-ready from day one." },
  { icon: TrendingUp, tag: "#PLUS", title: "Shopify Plus Development", desc: "Advanced customization, multi-store, checkout extensions, and B2B/wholesale for high-volume brands." },
  { icon: Layers, tag: "#HEADLESS", title: "Headless Commerce", desc: "React/Next.js storefronts on Shopify's Storefront API — full design freedom, top-tier speed." },
  { icon: Zap, tag: "#THEME", title: "Theme Customization", desc: "Custom or customized themes that match your brand and actually improve UX." },
  { icon: PlugZap, tag: "#APPS", title: "Custom App Development", desc: "Subscription systems, loyalty programs, configurators — whatever Shopify can't do out of the box." },
  { icon: RefreshCw, tag: "#MIGRATION", title: "Migration to Shopify", desc: "Full migration from WooCommerce, Magento, or custom platforms — zero data loss, SEO preserved." },
  { icon: Zap, tag: "#SPEED", title: "Speed Optimization", desc: "Core Web Vitals, image loading, app bloat — targeting 90+ mobile performance." },
  { icon: Search, tag: "#SEO", title: "Shopify SEO", desc: "Technical audits, structured data, and content strategy built for Shopify's platform quirks." },
  { icon: TrendingUp, tag: "#CRO", title: "Conversion Rate Optimization", desc: "Behavior analysis and A/B testing to turn more visitors into paying customers." },
];

const AI_USE_CASES = [
  { icon: MessageSquare, title: "AI Customer Support", desc: "Automated answers for order status, returns, and common questions — with a smooth handoff to your team when it matters." },
  { icon: PackageSearch, title: "Inventory & Order Sync", desc: "Keep stock levels, orders, and fulfillment in sync across Shopify, your warehouse, and other tools — automatically." },
  { icon: Sparkles, title: "Personalized Recommendations", desc: "Product suggestions and content tailored to real browsing and purchase behavior, not generic bestseller lists." },
  { icon: BarChart3, title: "Reporting & Alerts", desc: "Automated dashboards and alerts for sales trends, low stock, and abandoned carts — no manual digging required." },
];

const AI_STEPS = [
  { step: "01", label: "Identify the repetitive workflow" },
  { step: "02", label: "Define the inputs, outputs and approval points" },
  { step: "03", label: "Build and connect the automation" },
  { step: "04", label: "Test, document and hand over the workflow" },
];

const OTHER_SERVICES = [
  { icon: Layers, tag: "#WEB-DEV", title: "Website Development", blurb: "Custom websites and web apps — any stack, built around what your project actually needs.", points: ["Custom SaaS & web platforms", "Admin dashboards & internal tools", "Marketplace / multi-vendor builds", "Legacy app modernization"] },
  { icon: Terminal, tag: "#API", title: "API Development", blurb: "Secure, documented APIs that connect your store, tools, and systems.", points: ["REST & GraphQL API design", "Shopify API integrations", "Third-party integrations (CRM, ERP, payments)", "Auth (OAuth, JWT) & rate limiting"] },
  { icon: Cloud, tag: "#DEVOPS", title: "DevOps Support", blurb: "CI/CD, containers, and cloud infra so releases go out smoothly, every time.", points: ["CI/CD pipeline setup", "Docker & Kubernetes", "AWS / GCP / Azure infra", "Monitoring & alerting"] },
  { icon: Server, tag: "#SERVER-MGMT", title: "Server Management", blurb: "24/7 monitoring and maintenance so your apps stay online and fast.", points: ["Server setup & hardening", "Uptime monitoring 24/7", "Automated backups", "SSL management"] },
  { icon: Compass, tag: "#CONSULTING", title: "Tech Consulting", blurb: "Architecture and stack decisions, made right before you build.", points: ["Tech stack selection", "Codebase & security audits", "Scalability roadmaps", "Fractional CTO support"] },
];

export default function ServicesPage() {
  return (
    <div style={{ fontFamily: "'Inter', sans-serif", background: "#F6F4EE", color: "#0A0F1E" }}>
      <style>{JHALA_GLOBAL_CSS}</style>
      <TopStrip />
      <Header active="Services" />

      {/* PAGE HERO */}
      <section style={{ background: "#0A0F1E", color: "#F6F4EE", padding: "64px 24px 56px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(#23304A 1px, transparent 1px), linear-gradient(90deg, #23304A 1px, transparent 1px)", backgroundSize: "48px 48px", opacity: 0.2, maskImage: "radial-gradient(ellipse at 70% 30%, black, transparent 70%)" }} />
        <div style={{ maxWidth: 1180, margin: "0 auto", position: "relative" }}>
          <Reveal>
            <TagChip>$ jhalatech --services --list</TagChip>
            <h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "clamp(30px, 4.4vw, 46px)", lineHeight: 1.1, margin: "18px 0 14px" }}>
              What we do
            </h1>
            <p style={{ fontSize: 16, color: "#B8BFCF", maxWidth: 560, lineHeight: 1.65 }}>
              Ecommerce is our core specialty — but we're also the full-stack team behind the websites,
              APIs, and infrastructure that ecommerce alone can't cover.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ECOMMERCE FLAGSHIP */}
      <section style={{ padding: "80px 24px 60px", background: "#F6F4EE" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto" }}>
          <Reveal>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <TagChip tone="signal">#FLAGSHIP</TagChip>
              <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 14, fontWeight: 600, color: "#4CE0B3" }}>01 / core specialty</span>
            </div>
            <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(24px, 3vw, 34px)", fontWeight: 700, margin: "0 0 14px", maxWidth: 640 }}>
              Ecommerce & Shopify Development
            </h2>
            <p style={{ color: "#4A5468", fontSize: 15.5, maxWidth: 640, lineHeight: 1.7, marginBottom: 44 }}>
              Your Shopify store is the foundation of your online business. We build, customize, and optimize
              stores for brands that want more than a template — from first launch to Shopify Plus scale.
            </p>
          </Reveal>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }} className="ecom-services-grid">
            {ECOM_SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={(i % 3) * 0.06}>
                <div className="svc-card" style={{ border: "1px solid #DCD8CC", borderRadius: 8, padding: "24px 22px", background: "#FFFFFF", height: "100%" }}>
                  <s.icon size={20} style={{ color: "#FF5C39", marginBottom: 14 }} />
                  <div style={{ marginBottom: 10 }}><TagChip tone="signal">{s.tag}</TagChip></div>
                  <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 15.5, fontWeight: 600, margin: "0 0 6px" }}>{s.title}</h3>
                  <p style={{ color: "#6B7280", fontSize: 13.5, lineHeight: 1.6, margin: 0 }}>{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2}>
            <div style={{ marginTop: 36 }}>
              <Link to="/contact-us" className="cta-btn" style={{ background: "#FF5C39", color: "#0A0F1E", border: "none", padding: "13px 22px", borderRadius: 4, fontWeight: 600, fontSize: 14, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8, textDecoration: "none" }}>
                Get a Free Shopify Store Audit <ArrowUpRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* OTHER SERVICES - DETAILED */}
      <section style={{ padding: "40px 24px 90px", background: "#F6F4EE" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto" }}>
          <Reveal>
            <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 14, fontWeight: 600, color: "#4CE0B3" }}>02 / full-stack backbone</span>
            <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(22px, 2.8vw, 30px)", fontWeight: 700, margin: "10px 0 40px" }}>
              Beyond the storefront
            </h2>
          </Reveal>

          <div style={{ display: "flex", flexDirection: "column", gap: 1, background: "#DCD8CC" }}>
            {OTHER_SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.06}>
                <div style={{ background: "#FFFFFF", padding: "32px 28px", display: "grid", gridTemplateColumns: "1.2fr 1.8fr", gap: 28 }} className="other-svc-row">
                  <div>
                    <s.icon size={22} style={{ color: "#0A0F1E", marginBottom: 12 }} />
                    <div style={{ marginBottom: 10 }}><TagChip>{s.tag}</TagChip></div>
                    <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 19, fontWeight: 600, margin: "0 0 8px" }}>{s.title}</h3>
                    <p style={{ color: "#6B7280", fontSize: 14, lineHeight: 1.6, margin: 0 }}>{s.blurb}</p>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 20px", alignContent: "start" }}>
                    {s.points.map((pt) => (
                      <div key={pt} style={{ fontSize: 13.5, color: "#1A2233", display: "flex", gap: 8, alignItems: "flex-start" }}>
                        <span style={{ color: "#4CE0B3", fontFamily: "'IBM Plex Mono', monospace" }}>▸</span>{pt}
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CUSTOM AI AUTOMATION */}
      <section style={{ padding: "80px 24px", background: "#0A0F1E", color: "#F6F4EE" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto" }}>
          <Reveal>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <TagChip tone="signal">#AI-AUTOMATION</TagChip>
              <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 14, fontWeight: 600, color: "#4CE0B3" }}>03 / intelligent automation</span>
            </div>
            <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(24px, 3vw, 34px)", fontWeight: 700, margin: "0 0 14px", maxWidth: 640 }}>
              Custom AI Automation for Ecommerce
            </h2>
            <p style={{ color: "#B8BFCF", fontSize: 15.5, maxWidth: 640, lineHeight: 1.7, marginBottom: 44 }}>
              We design custom AI automations around the way your ecommerce business already works —
              planned around your tools, team, rules and approval process, instead of forcing a generic
              workflow onto your store.
            </p>
          </Reveal>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 48 }}>
            {AI_USE_CASES.map((u, i) => (
              <Reveal key={u.title} delay={i * 0.07}>
                <div style={{ background: "#0F1729", border: "1px solid #23304A", borderRadius: 8, padding: "22px 20px", height: "100%" }}>
                  <u.icon size={20} style={{ color: "#4CE0B3", marginBottom: 14 }} />
                  <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 15, fontWeight: 600, margin: "0 0 6px" }}>{u.title}</h3>
                  <p style={{ color: "#8B93A7", fontSize: 13, lineHeight: 1.6, margin: 0 }}>{u.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15}>
            <div style={{ background: "#0F1729", border: "1px solid #23304A", borderRadius: 8, padding: "8px 22px", maxWidth: 560 }}>
              {AI_STEPS.map((s, i) => (
                <div key={s.step} style={{ display: "flex", gap: 16, alignItems: "flex-start", padding: "18px 0", borderBottom: i < AI_STEPS.length - 1 ? "1px solid #23304A" : "none" }}>
                  <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 15, color: "#4CE0B3", flexShrink: 0 }}>{s.step}</span>
                  <span style={{ fontSize: 14, color: "#F6F4EE" }}>{s.label}</span>
                </div>
              ))}
              <div style={{ padding: "18px 0 8px" }}>
                <Link to="/contact-us" className="cta-btn" style={{ background: "#4CE0B3", color: "#0A0F1E", border: "none", padding: "11px 20px", borderRadius: 4, fontWeight: 600, fontSize: 13.5, cursor: "pointer", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8 }}>
                  Discuss an Ecommerce AI Automation <ArrowUpRight size={14} />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: "70px 24px", background: "#0A0F1E", color: "#F6F4EE", textAlign: "center", borderTop: "1px solid #23304A" }}>
        <Reveal>
          <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(22px, 3vw, 30px)", fontWeight: 700, margin: "0 0 14px" }}>
            Not sure which service fits your project?
          </h2>
          <p style={{ color: "#B8BFCF", fontSize: 15, marginBottom: 26 }}>
            Tell us what you're building — we'll point you to the right starting point.
          </p>
          <Link to="/contact-us" className="cta-btn" style={{ background: "#4CE0B3", color: "#0A0F1E", border: "none", padding: "13px 24px", borderRadius: 4, fontWeight: 600, fontSize: 14, cursor: "pointer", textDecoration: "none", display: "inline-block" }}>
            Book a Free Consultation
          </Link>
        </Reveal>
      </section>

      <Footer />
    </div>
  );
}
